import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { LoadFailure } from '../../components/LoadFailure';
import { logEvent } from '../../utils/eventLog';
import { formatUpdatedAgo } from '../../utils/updatedAgo';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { TopBar } from '../IndividualPlan/TopBar';
import { breadcrumbsFor, formatDay, pageHeaderFor, schoolRoute, teacherRoute, windowMode, withoutHiddenBenchmarks } from './benchmarkMonitoring';
import { BenchmarkMonitoringNotFound, fetchBenchmarkMonitoring, type MonitoringQuery } from './benchmarkMonitoringApi';
import { BenchmarksCard } from './components/BenchmarksCard';
import { GroupTable } from './components/GroupTable';
import { CopyEmailsButton, EmailTeacherButton, PageHeader } from './components/PageHeader';
import { StudentsTable } from './components/StudentsTable';
import { UpNextCard } from './components/UpNextCard';
import { BENCH } from './tokens';
import type { BenchmarkMonitoring } from './types';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export type MonitoringSource = (query: MonitoringQuery) => Promise<BenchmarkMonitoring>;

type Loaded = { kind: 'ready'; data: BenchmarkMonitoring } | { kind: 'notFound' } | { kind: 'failed' };

type Settled = { key: string; loaded: Loaded };

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function BenchmarksPage({ source = fetchBenchmarkMonitoring, now }: { source?: MonitoringSource; now?: Date } = {}) {
  const navigate = useNavigate();
  const { schoolId, teacherId } = useParams();
  const key = `${schoolId ?? ''}|${teacherId ?? ''}`;
  const [settled, setSettled] = useState<Settled | null>(null);
  const state: Loaded | { kind: 'loading' } = settled?.key === key ? settled.loaded : { kind: 'loading' };

  useEffect(() => {
    let active = true;
    source({ school: schoolId, teacher: teacherId })
      .then((data) => {
        if (active) setSettled({ key, loaded: { kind: 'ready', data: withoutHiddenBenchmarks(data) } });
      })
      .catch((err: unknown) => {
        if (!active) return;
        setSettled({ key, loaded: { kind: err instanceof BenchmarkMonitoringNotFound ? 'notFound' : 'failed' } });
      });
    return () => {
      active = false;
    };
  }, [source, schoolId, teacherId, key]);

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: BENCH.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="dashboard" />
      <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {state.kind === 'ready' && <TopBar crumbs={breadcrumbsFor(state.data)} />}
        <Box
          data-testid="benchmarks-content"
          sx={{ p: '24px 24px 72px', maxWidth: PAGE_MAX_WIDTH + 48, width: '100%', mx: 'auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '24px' }}
        >
          {state.kind === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: '64px' }}>
              <CircularProgress />
            </Box>
          )}
          {state.kind === 'failed' && <LoadFailure tokens={BENCH} />}
          {state.kind === 'notFound' && (
            <Typography data-testid="monitoring-not-found" sx={{ fontFamily: BENCH.font, fontSize: 16, lineHeight: '24px', color: BENCH.ink }}>
              {notFoundCopy(schoolId, teacherId)}
            </Typography>
          )}
          {state.kind === 'ready' && (
            <MonitoringBody
              data={state.data}
              now={now ?? new Date()}
              onSchool={(id) => {
                void logEvent('benchmark_monitoring.school_opened.v1').catch(() => {});
                navigate(schoolRoute(id));
              }}
              onTeacher={(id) => {
                void logEvent('benchmark_monitoring.teacher_opened.v1').catch(() => {});
                navigate(teacherRoute(state.data.school?.id ?? schoolId ?? '', id));
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

function notFoundCopy(schoolId: string | undefined, teacherId: string | undefined): string {
  if (teacherId) return 'Teacher not found.';
  if (schoolId) return 'School not found.';
  return 'Benchmark monitoring is not available for this district.';
}

function MonitoringBody({
  data,
  now,
  onSchool,
  onTeacher,
}: {
  data: BenchmarkMonitoring;
  now: Date;
  onSchool: (id: string) => void;
  onTeacher: (id: string) => void;
}) {
  const mode = windowMode(data);
  const header = pageHeaderFor(data, now);
  const between = data.window !== null && data.window.state === 'closed';
  const action =
    data.level === 'teacher' && data.teacher ? (
      <EmailTeacherButton firstName={data.teacher.firstName} email={data.teacher.email} />
    ) : (
      <CopyEmailsButton emails={data.contactEmails} open={mode.open} />
    );
  const cardRight =
    data.level === 'district'
      ? between && data.window
        ? `Window closed ${formatDay(data.window.closeAt)}`
        : undefined
      : plural(data.students, 'student', 'students');
  return (
    <>
      <PageHeader title={header.title} subtitle={header.subtitle} caption={formatUpdatedAgo(data.refreshedAt, now)} action={action} />
      {(data.level === 'district' ? !mode.open : data.window === null) && <UpNextCard upNext={data.upNext} now={now} />}
      {data.window && <BenchmarksCard data={data} right={cardRight} />}
      {data.window && data.level === 'district' && (
        <GroupTable title="Schools" data={data} groups={data.schools ?? []} onSelect={onSchool} emptyText="No schools have rostered students." />
      )}
      {data.window && data.level === 'school' && (
        <GroupTable title="Teachers" data={data} groups={data.teachers ?? []} onSelect={onTeacher} emptyText="No teachers have students at this school." />
      )}
      {data.window && data.level === 'teacher' && <StudentsTable data={data} students={data.studentRows ?? []} now={now} />}
    </>
  );
}
